import { Ajax, Ajax2 } from '@shared/Ajax';

function moveItems(destinationFolderId: number, folders = [], fileUuids: string[] = [], shortcuts = [], externals = []): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file_folder/move_files_and_images`,
        method: 'POST',
        data: {
            destinationFolderId,
            folders: folders.join(','),
            files: fileUuids.join(','),
            links: shortcuts.join(','),
            externals: externals.join(','),
        },
    });
}

function deleteItems(
    folderIds: number[] = [],
    fileUuids: string[] = [],
    linkIds: number[] = [],
    externalUuids: string[] = [],
    logpointsFolderUuid: string = null,
    permanentDelete = false
): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file_folder/delete_file_and_image`,
        method: 'POST',
        timeout: 120 * 1000,
        data: {
            folders: folderIds.join(','),
            files: fileUuids.join(','),
            links: linkIds.join(','),
            externals: externalUuids.join(','),
            logpointsFolder: logpointsFolderUuid ?? null,
        },
    });
}

function undeleteItems(uuids: string[]): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file_folder/undelete`,
        method: 'POST',
        timeout: 120 * 1000,
        data: {
            uuids,
        },
    });
}

function attachAlignment(folders = [], files = [], alignmentId: number, alignmentIndex: number): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file_folder/attach_alignment`,
        method: 'POST',
        data: {
            folders: folders.join(','),
            files: files.join(','),
            alignmentId,
            alignmentIndex,
        },
    });
}

function downloadItems(folders = [], files = [], shortcuts = []): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file_folder/download`,
        method: 'POST',
        data: {
            folders: folders.join(','),
            files: files.join(','),
            links: shortcuts.join(','),
        },
    });
}

function addComment(fileId: number, msg: string = ''): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file/${fileId}/comment.json`,
        method: 'POST',
        data: { text: msg },
    });
}

function getComments(fileId: number, page?: number, all?: boolean): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file/${fileId}/comment.json`,
        data: {
            page,
            all,
        },
    });
}

function createShortcut(fileId: number, folderId: number): JQueryPromise<any> {
    return Ajax({
        url: `api/1/link/create.json`,
        method: 'POST',
        data: {
            fileId,
            folderId,
        },
    });
}

function getVersions(fileId: number): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file/${fileId}/versions.json`,
    });
}

function setActiveVersion(fileId: number): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file/${fileId}/active_version.json`,
        method: 'POST',
    });
}

function getParseLog(fileId: number): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file/${fileId}/model_parse_logs.json`,
    });
}

function getProjectForFileId(fileId: number | string): JQueryPromise<{ id: number; name: string }> {
    return Ajax({
        url: `api/1/file/${fileId}/project.json`,
    }).then(resp => resp.project);
}

function getFileHistory(fileUuid: string): JQueryPromise<any> {
    return Ajax({
        url: `api/1/file/${fileUuid}/history.json`,
    });
}

export const FilesService = {
    moveItems,
    deleteItems,
    undeleteItems,
    attachAlignment,
    downloadItems,
    addComment,
    getComments,
    createShortcut,
    getVersions,
    setActiveVersion,
    getParseLog,
    getProjectForFileId,
    getFileHistory,

    isOfficeDocument(filename: string): boolean {
        return FilesService.isPhoto(filename) || /pdf|txt|doc|docx|ppt|pptx|ods|xls|xlsx|html/gi.test(utils.getExtensionFromFilename(filename));
    },

    isPhoto(filename: string): boolean {
        return /jpg|jpeg|bmp|png/gi.test(utils.getExtensionFromFilename(filename));
    },

    isOrtho(filename: string): boolean {
        const suffix = utils.getExtensionFromFilename(filename).toLowerCase();
        return suffix === 'tiff' || suffix === 'tif' || suffix === 'jp2';
    },

    getDetails(fileId: number): Promise<IFileDetails> {
        return Ajax2({
            url: `${kuura.getContextPath()}/api/1/file/${fileId}/details.json`,
        });
    },

    getDetailsByUuid(uuid: string): Promise<IFileDetails> {
        return Ajax2({
            url: `${kuura.getContextPath()}/api/1/file/details/${uuid}`,
        }).then(r => {
            return {
                status: true,
                parseLogs: [],
                layers: [],
                vehicles: [],
                ...r,
            };
        });
    },

    reparseFiles(fileUuids: string[]): Promise<any> {
        return Ajax2({
            url: `${kuura.getContextPath()}/api/1/document/clear-cache`,
            method: 'post',
            contentType: 'json',
            data: fileUuids,
        });
    },
};
